/**
 * Prints a page of its own (the menu, the stickers) from the app's own window, as KellerLog does
 * (print.ts there): a hidden frame does not do on the iPhone, so the page goes into the app's
 * document, in a shadow root (its styles and the app's stay apart), shown for the print only.
 *
 * The app on an iPhone's home screen cannot print at all: iOS ignores print() there. Then the menu
 * goes as a PDF (menuPdf.ts) to the share sheet, which offers „Drucken“ for every PDF; KellerLog
 * went the same way on 21.10.2026 (printPdf.ts there). The share sheet opens only right from a tap,
 * so the PDF is made first and `PrintSheet` (App.tsx) offers it with one more tap.
 *
 * A menu prints from its PDF everywhere (Martin, 23.10.2026: the printed page differed from the
 * PDF): the PDF's pages, drawn at 300 dpi, are the printed pages, at the paper size of the style.
 */

const HOST = "print-host";
const STYLE = "print-style";

/** Takes the page of the last print away again. */
let restore: (() => void) | null = null;

/** Safari (on the Mac; on the iPhone it prints from the share sheet), not Chrome or Edge, which also say „Safari“. */
export const isSafari = () => /Safari\//.test(navigator.userAgent) && !/Chrome\/|Chromium\/|Edg\/|CriOS\/|FxiOS\//.test(navigator.userAgent);

/** On the home screen of an iPhone or iPad. The browser tests set `speisekartePrintAsPdf` to walk that way too. */
export function printsAsPdf(): boolean {
  if ((window as { speisekartePrintAsPdf?: boolean }).speisekartePrintAsPdf) return true;
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
  const standalone = (navigator as { standalone?: boolean }).standalone === true || window.matchMedia("(display-mode: standalone)").matches;
  return ios && standalone;
}

/** The PDF being made or made, or why not. Shown by `PrintSheet` in App.tsx. */
export const printPdf: { busy: boolean; file: File | null; error: string | null } = { busy: false, file: null, error: null };
export const PRINT_PDF_EVENT = "speisekarte:print-pdf";
const told = () => window.dispatchEvent(new Event(PRINT_PDF_EVENT));

async function makePdf(name: string, pdf: () => Promise<Uint8Array>) {
  Object.assign(printPdf, { busy: true, file: null, error: null });
  told();
  try {
    const bytes = await pdf();
    Object.assign(printPdf, { busy: false, file: new File([bytes as BlobPart], name, { type: "application/pdf" }) });
  } catch (e) {
    Object.assign(printPdf, { busy: false, error: `Das PDF ließ sich nicht erstellen${e instanceof Error ? `: ${e.message}` : "."}` });
  }
  told();
}

export function closePrintPdf() {
  Object.assign(printPdf, { busy: false, file: null, error: null });
  told();
}

/** Hands the PDF to the share sheet (there: „Drucken“); where that is missing, saves it. */
export async function sharePrintPdf() {
  const file = printPdf.file;
  if (!file) return;
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: file.name.replace(/\.pdf$/, "") });
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) throw e;
    }
    return;
  }
  downloadBlob(file, file.name);
}

export function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Safari prints the PDF itself, in a tab of its own (Martin, 23.10.2026): its print of a page of
 * pictures came out a page too long however small they were (100 % one page, 101 % two). Safari's
 * own PDF print fits the page to the paper and the printer's margins and prints the type as type.
 *
 * The tab opens right in the tap (later Safari would block it), showing a line while the PDF is
 * made; then the PDF, and its print dialog. Without the tab (blocked) the pictures print here.
 */
export function pdfTab(): Window | null {
  if (!isSafari() || printsAsPdf()) return null;
  const tab = window.open("", "_blank");
  try {
    tab?.document.write(`<!doctype html><meta charset="utf-8"><title>Speisekarte</title><p style="font:16px system-ui;margin:2rem;color:#555">PDF wird erstellt …</p>`);
  } catch {
    // a tab of its own origin: only the line is missing
  }
  return tab;
}

/** The PDF in the tab of `pdfTab`, then its print dialog. */
export function printInTab(tab: Window, bytes: Uint8Array) {
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: "application/pdf" }));
  tab.location.href = url;
  // Safari's PDF view prints the PDF; once it is there, its dialog.
  const started = Date.now();
  const timer = setInterval(() => {
    let ready = false;
    try {
      ready = tab.location.href === url && tab.document.readyState === "complete";
    } catch {
      ready = false;
    }
    if (ready || tab.closed || Date.now() - started > 15_000) {
      clearInterval(timer);
      if (ready && !tab.closed) {
        try {
          tab.focus();
          tab.print();
        } catch {
          // ⌘P in the tab does the same
        }
      }
    }
  }, 200);
  setTimeout(() => URL.revokeObjectURL(url), 10 * 60_000);
}

/**
 * Prints a PDF as it is: its pages drawn as pictures (pdf.js, 300 dpi), one per printed page of the
 * PDF's size, without margins of the browser. On an iPhone's home screen the PDF goes to the share
 * sheet instead.
 */
export async function printPdfPages(pdf: { name: string; make: () => Promise<Uint8Array> }): Promise<void> {
  if (printsAsPdf()) return makePdf(pdf.name, pdf.make);
  const [bytes, pdfjs, worker] = await Promise.all([pdf.make(), import("pdfjs-dist"), import("pdfjs-dist/build/pdf.worker.min.mjs?url")]);
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  const task = pdfjs.getDocument({ data: bytes });
  try {
    const doc = await task.promise;
    const pages: { url: string; w: number; h: number }[] = [];
    for (let n = 1; n <= doc.numPages; n++) {
      const page = await doc.getPage(n);
      const size = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: 300 / 72 });
      const canvas = document.createElement("canvas");
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      await page.render({ canvas, viewport }).promise;
      const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/png"));
      if (!blob) throw new Error("Die Seite ließ sich nicht zeichnen.");
      pages.push({ url: URL.createObjectURL(blob), w: size.width, h: size.height });
    }
    // Chrome and Firefox print on the paper of the style without a margin of their own. Safari keeps
    // the margins of the printer and the paper of its dialog: a page as large as the paper does not
    // fit there, and its rest made a second, empty page (Martin, 23.10.2026). So in Safari each page
    // is a little smaller and centred across, with no space above: Safari's own margin is that space
    // (an added one made the page a point too tall again). 92 % filled what Safari prints to the
    // point (Martin: at 101 % a page came again), so 90 %, a few millimetres to spare.
    const k = isSafari() ? 0.9 : 1;
    const first = pages[0] ?? { w: 595.28, h: 841.89 };
    const html = `<!doctype html><html><head><title>${pdf.name.replace(/\.pdf$/, "").replace(/</g, "&lt;")}</title><style>
@page { size: ${first.w}pt ${first.h}pt; margin: 0; }
html, body { margin: 0; padding: 0; }
.sheet { break-after: page; break-inside: avoid; }
.sheet:last-child { break-after: auto; }
.sheet img { display: block; margin: 0 auto; }
</style></head><body>${pages.map((p) => `<div class="sheet"><img src="${p.url}" alt="" style="width:${(p.w * k).toFixed(2)}pt;height:${(p.h * k).toFixed(2)}pt"></div>`).join("")}</body></html>`;
    await show(html);
  } finally {
    void task.destroy();
  }
}

/** The page into the app's document for the print, then the browser's print dialog. */
async function show(html: string) {
  restore?.();
  restore = null;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const css = [...doc.querySelectorAll("style")].map((s) => s.textContent ?? "").join("\n");
  // The page's size and margins only work in the document itself; the rest stays in the shadow root,
  // where `body` becomes the wrapper that carries the page's own body class.
  const pages = css.match(/@page[^{]*\{[^}]*\}/g) ?? [];
  const inner = css
    .replace(/@page[^{]*\{[^}]*\}/g, "")
    .replace(/@media screen\s*\{[^{}]*(\{[^}]*\}[^{}]*)*\}/g, "")
    .replace(/(^|[\s,{}(])(?:html|body)(?=[\s{.,:>)[])/g, "$1.print-body");

  const host = document.createElement("div");
  host.id = HOST;
  const root = host.attachShadow({ mode: "open" });
  root.innerHTML = `<style>${inner}</style><div class="print-body ${doc.body.className}">${doc.body.innerHTML}</div>`;
  const style = document.createElement("style");
  style.id = STYLE;
  style.textContent = `${pages.join("\n")}
@media screen { #${HOST} { display: none; } }
@media print {
  html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; height: auto !important; min-height: 0 !important; overflow: visible !important; }
  body > *:not(#${HOST}) { display: none !important; }
}`;
  document.head.appendChild(style);
  document.body.appendChild(host);

  // The title names the file when the print is saved as PDF.
  const title = document.title;
  if (doc.title) document.title = doc.title;
  const back = () => {
    document.title = title;
    window.removeEventListener("afterprint", back);
  };
  // The page stays (hidden on the screen) until the next print: Safari on the iPhone may report
  // the end of printing before it has laid out the page.
  restore = () => {
    back();
    host.remove();
    style.remove();
  };
  window.addEventListener("afterprint", back);
  // Pictures first, or the print may come without them.
  await Promise.all([...root.querySelectorAll("img")].map((img) => img.decode().catch(() => undefined)));
  window.print();
}
